import React from 'react';
import { useToast } from '../hooks/use-toast';
import { monitoring } from '../lib/monitoring';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Check if this is a module loading error
    const isModuleLoadingError = 
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('ChunkLoadError') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk');
    
    if (isModuleLoadingError) {
      console.warn('Module loading error detected. This might be fixed by refreshing the page.');
      // You could trigger a page refresh here if appropriate
      // window.location.reload();
    }
    
    monitoring.captureError(error, errorInfo.componentStack || '', {
      type: 'react_error_boundary',
      component: errorInfo.componentStack?.split('\n')[1]?.trim() || 'unknown',
      isModuleLoadingError: isModuleLoadingError ? 'true' : 'false'
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }: { error: Error | null }) {
  const { toast } = useToast();
  const isModuleError = error?.message?.includes('Failed to fetch dynamically imported module') || 
                        error?.message?.includes('Importing a module script failed') ||
                        error?.message?.includes('ChunkLoadError');

  React.useEffect(() => {
    toast({
      title: isModuleError ? "Module loading error" : "An error occurred",
      description: isModuleError 
        ? "Failed to load a required component. Please clear your browser cache and refresh the page." 
        : (error?.message || "Please try refreshing the page"),
      variant: "destructive",
      //duration: 8000, // Show longer for more visibility
    });
  }, [error, toast, isModuleError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full p-8 rounded-lg shadow-lg bg-card">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          {isModuleError ? "Module Loading Error" : "Oops! Something went wrong"}
        </h2>
        
        {isModuleError ? (
          <>
            <p className="text-muted-foreground mb-4">
              The application failed to load a required component. This is often caused by:
            </p>
            <ul className="list-disc pl-5 mb-6 text-muted-foreground">
              <li>Outdated cached files in your browser</li>
              <li>Network issues while loading resources</li>
              <li>A recent application update</li>
            </ul>
          </>
        ) : (
          <p className="text-muted-foreground mb-6">
            We're sorry for the inconvenience. Please try refreshing the page or contact support if the problem persists.
          </p>
        )}
        
        <div className="space-y-3">
          <button
            onClick={() => {
              // For module errors, try clearing cache first
              if (isModuleError) {
                // Clear cache for this domain if possible
                if ('caches' in window) {
                  caches.keys().then(names => {
                    names.forEach(name => {
                      caches.delete(name);
                    });
                  });
                }
              }
              window.location.reload();
            }}
            className="w-full px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isModuleError ? "Clear Cache & Refresh" : "Refresh Page"}
          </button>
          
          <a
            href="/"
            className="block text-center w-full px-4 py-2 rounded bg-secondary text-secondary-foreground hover:bg-secondary/90"
          >
            Go to Home Page
          </a>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
